import { useCxSettings, useCxGlobalSettings, useCxCompanyPayers } from '@hooks/useCallExperience';
import { parseApiError } from '@utils/errorUtils';
import { useCxPrefs } from './cxFormat';

const IVR_OPTION_LABELS = {
  MaxDobAttempts: 'Birth-year attempts',
  MaxInputAttempts: 'Attempts per question',
  DobTimeoutSeconds: 'Birth-year timeout (s)',
  ConsentTimeoutSeconds: 'Consent timeout (s)',
  SupplyItemTimeoutSeconds: 'Supply item timeout (s)',
  SupplyPeriodTimeoutSeconds: 'Supply period timeout (s)',
  OptionalTimeoutSeconds: 'Optional question timeout (s)',
  DisclaimerTimeoutSeconds: 'Disclaimer timeout (s)',
  DefaultSupplyPeriodDays: 'Default supply period (days)',
};

const yesNo = (b) => (b ? 'Yes' : 'No');
const time = (t) => (t ? t.slice(0, 5) : 'not set');

function Kv({ k, v, why, col }) {
  const { technical } = useCxPrefs();
  return (
    <div className="cx-kv">
      <span className="cx-k">{k}</span>
      <span className="cx-v">{v}</span>
      {why && <span className="cx-why">{why}</span>}
      {col && technical && <code className="cx-col">{col}</code>}
    </div>
  );
}

function Flag({ on, label, col }) {
  const { technical } = useCxPrefs();
  return (
    <span className={`cx-badge ${on ? 'cx-badge-co' : 'cx-badge-gl'}`} title={technical && col ? col : undefined}>
      {on ? '✓' : '–'} {label}
    </span>
  );
}

/** The company's most common primary insurances (counts only) and how each changes the resupply call. */
function PayersCard({ companyId }) {
  const { data, isLoading, error } = useCxCompanyPayers(companyId);
  return (
    <div className="cx-card cx-card-wide">
      <h3>Insurance rules (top payers)</h3>
      <div className="cx-why cx-block">
        How each insurance changes the resupply call for the company&apos;s IVR patients. Autoship is asked when any of a
        patient&apos;s insurances asks it; the education statement follows the primary insurance only.
      </div>
      {error && <div className="cx-error">{parseApiError(error)}</div>}
      {isLoading && <span className="cx-muted">Loading insurances…</span>}
      {data && data.payers.length === 0 && <span className="cx-muted">No IVR patients with a primary insurance.</span>}
      {data?.payers.map((p) => (
        <div key={p.insuranceId} className="cx-payer">
          <div className="cx-payer-head"><b>{p.name}</b> <span className="cx-muted cx-small">{p.primaryPatientCount} patients</span></div>
          <div className="cx-row">
            <Flag on={p.asksAutoship} label="Asks autoship" col="bIVRAutoShip" />
            <Flag on={p.readsEducationStatement} label="Education statement" col="bIVREducationStatement" />
            {p.productLines.map((pl) => (
              <span key={pl.productLineId} className="cx-row">
                <Flag on={pl.allowsNinetyDaySupply} label={`${pl.productLineName}: 90-day supply`} col="S3_InsuranceCompanyProductLines.bAllowsNinetyDaySupply" />
                {pl.productLineId === 1 && <Flag on={pl.countsMaskAsSeal} label="Mask counts as seal" col="bCountsMaskAsSeal" />}
              </span>
            ))}
          </div>
          {p.excludedItemTypes.length > 0 && <div className="cx-small cx-muted">Never covered: {p.excludedItemTypes.join(', ')}</div>}
        </div>
      ))}
      {data && data.patientsWithoutPrimaryInsurance > 0 && (
        <div className="cx-callout cx-callout-warn cx-mt">
          {data.patientsWithoutPrimaryInsurance} of {data.totalIvrPatients} IVR patients have no primary insurance, so their call ends with &quot;nothing eligible&quot;.
        </div>
      )}
    </div>
  );
}

function GlobalCard({ global }) {
  if (!global) return null;
  return (
    <>
      <div className="cx-card">
        <h3>IVR timing (every company)</h3>
        {Object.entries(global.ivrOptions).map(([name, value]) => (
          <Kv key={name} k={IVR_OPTION_LABELS[name] || name} v={value} col={`PhoneSystem:Ivr:${name}`} />
        ))}
      </div>
    </>
  );
}

/** Read-only summary of the settings that change what a company's callers hear. */
export default function SettingsSummary({ ctx, onSelect, onToggle, togglePending }) {
  const { technical } = useCxPrefs();
  const { data: settings, isLoading, error } = useCxSettings(ctx.companyId);
  const { data: global } = useCxGlobalSettings();

  if (!ctx.companyId) {
    return (
      <div>
        <h2 className="cx-jtitle">⚙ Settings: global options</h2>
        <div className="cx-callout">These apply to every company. View a company to see its own settings. Nothing here is editable on this page.</div>
        <div className="cx-cards"><GlobalCard global={global} /></div>
      </div>
    );
  }

  if (error) return <div className="cx-error">{parseApiError(error)}</div>;
  if (isLoading || !settings) return <div className="cx-muted">Loading settings…</div>;

  if (settings.isParent) {
    return (
      <div>
        <h2 className="cx-jtitle">⚙ Settings: {settings.name}</h2>
        <div className="cx-callout">A parent group has no phone settings of its own. Its messages apply to these member companies unless a member has its own wording.</div>
        <div className="cx-cards">
          <div className="cx-card">
            <h3>Member companies ({settings.members.length})</h3>
            {settings.members.map((m) => (
              <div key={m.id} className="cx-kv">
                <button type="button" className="cx-link cx-k" onClick={() => onSelect(String(m.id))}>{m.name}</button>
                <span className="cx-v">{m.active ? m.id : `${m.id} · inactive`}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const ivr = settings.ivr;
  return (
    <div>
      <h2 className="cx-jtitle">⚙ Settings: {settings.name}</h2>
      <div className="cx-callout">What changes the path for {settings.name}&apos;s callers. Read-only; change these where the company is set up.</div>
      <div className="cx-cards">
        <div className="cx-card">
          <h3>Inbound</h3>
          <Kv k="Team queue" v={settings.teamName || 'none'} col="S3_Company.fkiTeamID" />
          <Kv k="Open (routing hours)" v={`${time(settings.routingHoursStart)}–${time(settings.routingHoursEnd)} CT`} col="dtOutReachStartTime / EndTime"
            why="Outside these hours callers hear the closed message. No day-of-week check." />
          <Kv k="Hours read on hold" v={`${time(settings.officeHoursStart)}–${time(settings.officeHoursEnd)}`} col="dtOfficeHours*, else the routing hours" />
          <Kv k="Calls recorded" v={yesNo(settings.recordsCalls)} col="S3_CompanySettings.bRecordsCalls" />
          <Kv k="Press 1 for the IVR from the queue" v={yesNo(settings.allowIvrFromQueue)} col="bAllowIVRTransferFromQueue" />
          <Kv k="Contracted (offshore) coaches may answer" v={yesNo(settings.allowsContractedInbound)} col="bAllowsContractedInbound"
            why={settings.allowsContractedInbound ? undefined : 'Offshore-restricted patients go to voicemail instead of the queue.'} />
          <Kv k="Deductible reminder (Oct–Dec)" v={settings.notifyDeductibleRenewing ? `Yes${settings.deductibleReminderDate ? `, last day ${settings.deductibleReminderDate}` : ''}` : 'No'}
            col="bNotifyDeductibleRenewingJanuary / dDeductibleReminder" />
          <Kv k="Greets with the branch name" v={yesNo(settings.readsBranchNames)} col="S3_Company.bReadsBranchNames" />
        </div>
        <div className="cx-card">
          <h3>Resupply IVR</h3>
          <Kv k="IVR" v={!ivr.enabled ? 'Off' : ivr.paused ? 'Paused' : 'On'} col="bIVRCompany / bPauseIVR" />
          <Kv k="Calling window" v={`${time(ivr.windowStart)}–${time(ivr.windowEnd)}`} col="dtIVRStartTime / EndTime" />
          <Kv k="Order goal" v={ivr.orderGoal} col="iIVROrderGoal" why={ivr.orderGoal === 0 ? 'A goal of 0 blocks all outreach calls.' : undefined} />
          <Kv k="Concurrent calls" v={ivr.maxConcurrentCalls} col="iMaxTotalIVRThreadsPerRequest (default when unset, capped)" />
          <Kv k="Representative transfer" v={ivr.repTransferAvailable ? ivr.transferNumber : 'Not offered'}
            col="sIVRCompanyTransferNumber + bAllowIVRPatientsTeamQueueOption"
            why={ivr.repTransferAvailable ? undefined : !ivr.teamQueueOption ? 'The team-queue option is off.' : 'No transfer number is set.'} />
          <Kv k="Address-change task" v={yesNo(ivr.addressChangeTask)} col="bAddressChangeTask" />
        </div>
        <div className="cx-card">
          <h3>Product lines</h3>
          {settings.productLines.length === 0 && <span className="cx-muted">None</span>}
          {settings.productLines.map((p) => <Kv key={p.id} k={p.name} v={p.active ? 'Active' : 'Inactive'} col="S3_CompanyProductLines" />)}
        </div>
        <div className="cx-card">
          <h3>Questions turned off ({settings.turnedOffQuestions.length})</h3>
          {settings.turnedOffQuestions.length === 0 && <span className="cx-muted">Every question is asked.</span>}
          {settings.turnedOffQuestions.map((q) => (
            <div key={q.identifier} className="cx-kv">
              <span className="cx-k">{q.label}</span>
              <button type="button" className="cx-btn cx-btn-small" disabled={togglePending} onClick={() => onToggle(q.identifier, true)}>Turn on</button>
              {technical && <code className="cx-col">{q.identifier}</code>}
            </div>
          ))}
        </div>
        <PayersCard companyId={ctx.companyId} />
        <GlobalCard global={global} />
      </div>
    </div>
  );
}
